import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePsychologistContext } from '@/contexts/PsychologistContext';
import { fetchInactivityRate, fetchInactivityDetail, rateFromDetail } from '@/hooks/api/psychologist/inactivity';
import type { InactivityRateData, InactivityDetailData, InactivityDetailProcess } from '@/hooks/api/psychologist/inactivity';
import { PENDING_DISCHARGED_STATUS_ID } from '@/utils/psychologistStatus';
import {
  ChevronDown,
  ChevronUp,
  TrendingDown,
  Users,
  XCircle,
  BarChart2,
  Check,
  X,
} from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { ProcessStatesInfo } from '@/components/common/ProcessStatesInfo';

// ─── Constants & helpers ──────────────────────────────────────────────────────

const MONTH_ES_TO_NUM: Record<string, string> = {
  enero: '01', febrero: '02', marzo: '03', abril: '04', mayo: '05', junio: '06',
  julio: '07', agosto: '08', septiembre: '09', octubre: '10', noviembre: '11', diciembre: '12',
};

function monthLabelToYYYYMM(label: string): string {
  const [mesName, year] = label.toLowerCase().split(' ');
  return `${year}-${MONTH_ES_TO_NUM[mesName] ?? '01'}`;
}

function prevMonthYYYYMM(yyyymm: string): string {
  const [y, m] = yyyymm.split('-').map(Number);
  const d = new Date(y, m - 2);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const ROL_TO_EXCLUSION: Record<string, ExclusionReason> = {
  excluido_por_sesiones: 'Proceso maduro',
  excluidos_sesiones:    'Proceso maduro',
  excluido_derivacion:   'Derivación exitosa',
  excluido_reactivado:   'Reactivado',
};

function patientName(p: InactivityDetailProcess): string {
  return p.patient_name?.trim() || p.process;
}
function sessionCount(p: InactivityDetailProcess): number {
  return p.total_sesiones ?? 0;
}
function formatISODate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  } catch { return '—'; }
}
function formatRate(rate: number): string {
  return (rate * 100).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + '%';
}
function isRateOk(rate: number): boolean { return rate <= 0.1; }

// ─── Types ────────────────────────────────────────────────────────────────────

type ExclusionReason = 'Proceso maduro' | 'Derivación exitosa' | 'Reactivado';
type BajaReason = 'Inactivo por tiempo' | 'Derivación no exitosa';
type PanelView = null | 'proceso' | 'porque' | 'concordancia' | 'exclusiones' | 'universo' | 'bajas' | 'excluidos' | 'todos' | 'objetivo';

interface InactividadProcess {
  id: string; patientName: string; lastSessionDate: string; sessionCount: number;
}
interface InactividadBaja {
  id: string; patientName: string; lastSessionDate: string; sessionCount: number; reason: BajaReason;
}
interface InactividadExcluido {
  id: string; patientName: string; exclusionReason: ExclusionReason; sessionCount: number; lastSessionDate: string;
}
interface MonthData {
  monthLabel: string; mesReporte: string; mesEvaluado: string;
  denominator: InactividadProcess[]; numerator: InactividadBaja[];
  excluded: InactividadExcluido[]; rate: number;
}
// ─── Mock data ────────────────────────────────────────────────────────────────

const MOCK_MAYO: MonthData = {
  monthLabel: 'mayo 2026', mesReporte: 'mayo', mesEvaluado: 'abril',
  denominator: [
    { id: 'd1',  patientName: 'Valentina García',   lastSessionDate: '22/04/2026', sessionCount: 18 },
    { id: 'd2',  patientName: 'Lucas Martínez',     lastSessionDate: '29/04/2026', sessionCount: 34 },
    { id: 'd3',  patientName: 'Sofía Rodríguez',    lastSessionDate: '15/04/2026', sessionCount: 12 },
    { id: 'd4',  patientName: 'Mateo López',        lastSessionDate: '08/04/2026', sessionCount: 22 },
    { id: 'd5',  patientName: 'Camila Fernández',   lastSessionDate: '25/04/2026', sessionCount: 28 },
    { id: 'd6',  patientName: 'Tomás Ruiz',         lastSessionDate: '30/04/2026', sessionCount: 9  },
    { id: 'd7',  patientName: 'Isabella Morales',   lastSessionDate: '17/04/2026', sessionCount: 41 },
    { id: 'd8',  patientName: 'Santiago Díaz',      lastSessionDate: '03/04/2026', sessionCount: 6  },
    { id: 'd9',  patientName: 'Lucía Herrera',      lastSessionDate: '24/04/2026', sessionCount: 16 },
    { id: 'd10', patientName: 'Emilio Castro',      lastSessionDate: '10/04/2026', sessionCount: 11 },
    { id: 'd11', patientName: 'Renata Vargas',      lastSessionDate: '28/04/2026', sessionCount: 29 },
    { id: 'd12', patientName: 'Agustín Méndez',     lastSessionDate: '14/04/2026', sessionCount: 15 },
    { id: 'd13', patientName: 'Juliana Ortega',     lastSessionDate: '21/04/2026', sessionCount: 23 },
    { id: 'd14', patientName: 'Maximiliano Reyes',  lastSessionDate: '07/04/2026', sessionCount: 5  },
    { id: 'd15', patientName: 'Florencia Silva',    lastSessionDate: '16/04/2026', sessionCount: 13 },
    { id: 'd16', patientName: 'Bruno Torres',       lastSessionDate: '23/04/2026', sessionCount: 20 },
    { id: 'd17', patientName: 'Catalina Gómez',     lastSessionDate: '09/04/2026', sessionCount: 8  },
    { id: 'd18', patientName: 'Ignacio Ríos',       lastSessionDate: '26/04/2026', sessionCount: 35 },
    { id: 'd19', patientName: 'Pilar Navarro',      lastSessionDate: '02/04/2026', sessionCount: 10 },
    { id: 'd20', patientName: 'Leandro Figueroa',   lastSessionDate: '18/04/2026', sessionCount: 19 },
    { id: 'd21', patientName: 'Martina Romero',     lastSessionDate: '25/04/2026', sessionCount: 26 },
    { id: 'd22', patientName: 'Sebastián Cruz',     lastSessionDate: '11/04/2026', sessionCount: 7  },
    { id: 'd23', patientName: 'Valeria Ibáñez',     lastSessionDate: '04/04/2026', sessionCount: 14 },
    { id: 'd24', patientName: 'Rodrigo Molina',     lastSessionDate: '30/04/2026', sessionCount: 11 },
    { id: 'd25', patientName: 'Jimena Soto',        lastSessionDate: '19/04/2026', sessionCount: 21 },
    { id: 'd26', patientName: 'Pablo Mendoza',      lastSessionDate: '27/04/2026', sessionCount: 9  },
    { id: 'd27', patientName: 'Carmen Aguilar',     lastSessionDate: '13/04/2026', sessionCount: 33 },
    { id: 'd28', patientName: 'Eduardo Paredes',    lastSessionDate: '06/04/2026', sessionCount: 6  },
    { id: 'd29', patientName: 'Natalia Fuentes',    lastSessionDate: '20/04/2026', sessionCount: 18 },
    { id: 'd30', patientName: 'Andrés Cabrera',     lastSessionDate: '12/04/2026', sessionCount: 16 },
    { id: 'd31', patientName: 'Florencia Ávila',    lastSessionDate: '29/04/2026', sessionCount: 12 },
  ],
  numerator: [
    { id: 'n1', patientName: 'Martina Torres', lastSessionDate: '14/04/2026', sessionCount: 7,  reason: 'Inactivo por tiempo' },
    { id: 'n2', patientName: 'Nicolás Pérez',  lastSessionDate: '28/04/2026', sessionCount: 4,  reason: 'Derivación no exitosa' },
  ],
  excluded: [
    { id: 'e1', patientName: 'Ana Gutiérrez', exclusionReason: 'Proceso maduro', sessionCount: 31, lastSessionDate: '09/04/2026' },
    { id: 'e2', patientName: 'Diego Sánchez', exclusionReason: 'Derivación exitosa',         sessionCount: 12, lastSessionDate: '22/04/2026' },
  ],
  rate: 2 / 31,
};

const MONTH_NUM_TO_ES: Record<string, string> = Object.fromEntries(
  Object.entries(MONTH_ES_TO_NUM).map(([k, v]) => [v, k])
);

function buildCurrentMonthData(): MonthData {
  const now = new Date();
  const year = now.getFullYear();
  const monthNum = now.getMonth() + 1;
  const monthES = MONTH_NUM_TO_ES[String(monthNum).padStart(2, '0')];
  const prevMonthNum = monthNum === 1 ? 12 : monthNum - 1;
  const prevMonthES = MONTH_NUM_TO_ES[String(prevMonthNum).padStart(2, '0')];
  return {
    monthLabel: `${monthES} ${year}`,
    mesReporte: monthES,
    mesEvaluado: prevMonthES,
    denominator: [], numerator: [], excluded: [], rate: 0,
  };
}

const STATIC_MONTHS: MonthData[] = [MOCK_MAYO];

function buildAvailableMonths(): MonthData[] {
  const current = buildCurrentMonthData();
  if (STATIC_MONTHS.find(m => m.monthLabel === current.monthLabel)) return STATIC_MONTHS;
  return [current, ...STATIC_MONTHS];
}

const AVAILABLE_MONTHS: MonthData[] = buildAvailableMonths();

// ─── Side Panel ───────────────────────────────────────────────────────────────
// Siempre montado. Portal en document.body → nunca afectado por transform de padres.
// Sin overlay → sin compositing layers extra → sin titilado.

interface SidePanelProps {
  view: PanelView;
  onClose: () => void;
  onBack: () => void;
  canGoBack: boolean;
  data: MonthData;
  onOpenView: (v: PanelView) => void;
  onGoToAsignacion?: () => void;
}

const PANEL_TITLES: Record<Exclude<PanelView, null>, string> = {
  proceso:      '¿Qué es un proceso terapéutico?',
  porque:       '¿Por qué importa esta métrica?',
  concordancia: '¿Por qué el reporte mide el mes anterior?',
  exclusiones:  '¿Qué procesos se excluyen y por qué?',
  universo:     '¿Qué es el universo a evaluar?',
  bajas:        '¿Qué son las bajas del mes?',
  excluidos:    '¿Qué procesos se excluyen?',
  todos:        'Todos los conceptos',
  objetivo:     'Mi tasa del 10% me garantiza más pacientes?',
};

const SidePanel: React.FC<SidePanelProps> = ({ view, onClose, onBack, canGoBack, data, onOpenView, onGoToAsignacion }) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Scroll al inicio del panel cada vez que cambia la vista
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [view]);

  const isOpen = view !== null;
  const title = view ? PANEL_TITLES[view] : '';

  const Link: React.FC<{ v: PanelView; children: React.ReactNode }> = ({ v, children }) => (
    <button
      type="button"
      onClick={() => onOpenView(v)}
      className="text-violet-600 underline underline-offset-2 font-semibold hover:text-violet-800 transition-colors"
    >
      {children}
    </button>
  );

  const renderContent = () => {
    switch (view) {

      case 'proceso':
        return <ProcessStatesInfo />;

      case 'porque':
        return (
          <div className="flex flex-col gap-4 text-sm text-gray-700 leading-relaxed">
            <p>
              La continuidad de tus pacientes es lo más cercano que tenemos a una señal objetiva de que el
              vínculo terapéutico se sostiene — y es lo que permite que Psi pueda seguir creciendo y
              asignándote nuevos pacientes.
            </p>
            <p>
              Sabemos que hay infinitas circunstancias que pueden atravesar a un proceso terapéutico,
              resultando en la pausa o finalización del mismo, a veces por motivos que exceden a cualquiera
              de las partes. Por eso, definimos una regla ecuánime para todas: si pasaron 30 días sin sesión,
              el proceso pasa a{' '}
              <Link v="proceso">inactivo</Link>
              .{' '}
              Es importante aclarar que tanto vos como todas tus colegas está igual de expuestas a que — por
              cualquier causa — un proceso pase a{' '}
              <Link v="proceso">Inactivo</Link>
              {' '}— por eso es una métrica justa para todxs.
            </p>
            <p className="text-gray-500">
              (Lo que sí tiene en cuenta:{' '}
              <Link v="exclusiones">derivaciones exitosas</Link>,{' '}
              <Link v="exclusiones">procesos maduros</Link>,{' '}
              <Link v="exclusiones">reactivaciones</Link>. Esos casos se excluyen del cálculo — clickeá en
              cada concepto para saber más.)
            </p>
            <div className="rounded-xl bg-violet-50 border border-violet-100 px-4 py-3 flex flex-col gap-1.5">
              <p className="text-violet-800 font-semibold">¿En qué me afecta concretamente?</p>
              <p className="text-violet-700">
                Es uno de los factores más importantes que define{' '}
                <strong>cuántos pacientes nuevos podés recibir</strong>. Mantenerla por debajo del{' '}
                <button
                  type="button"
                  onClick={() => onOpenView('objetivo')}
                  className="underline underline-offset-2 font-semibold hover:opacity-70 transition-opacity"
                >
                  10% mensual
                </button>{' '}
                te prioriza en la asignación. Aprendé más sobre esto en{' '}
                <button
                  type="button"
                  onClick={() => { onGoToAsignacion?.(); onClose(); }}
                  className="underline underline-offset-2 font-semibold hover:opacity-70 transition-opacity"
                >
                  Asignaciones
                </button>
              </p>
            </div>
          </div>
        );

      case 'concordancia':
        return (
          <div className="flex flex-col gap-4 text-sm text-gray-700 leading-relaxed">
            <p>
              Cuando seleccionás <strong className="capitalize">{data.mesReporte}</strong> en el filtro, estás viendo el reporte
              generado en {data.mesReporte} — que mide qué pasó en <strong className="capitalize">{data.mesEvaluado}</strong> (el mes anterior).
            </p>
            <div className="rounded-xl bg-sky-50 border border-sky-100 px-4 py-3 text-sm text-sky-800 font-medium">
              Filtro = <span className="capitalize">{data.mesReporte}</span> → mide lo que pasó en <span className="capitalize">{data.mesEvaluado}</span>.
            </div>
            <div className="border-t border-gray-100 pt-3 flex flex-col gap-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Cómo se calcula</p>
              {/* Numerador primero */}
              <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 flex flex-col gap-1">
                <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Numerador</p>
                <p className="font-medium text-gray-800">Procesos que pasaron a inactivo durante <span className="capitalize">{data.mesEvaluado}</span></p>
              </div>
              {/* Denominador */}
              <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 flex flex-col gap-1">
                <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Denominador</p>
                <p className="font-medium text-gray-800">Procesos activos al inicio de <span className="capitalize">{data.mesEvaluado}</span></p>
              </div>
              {/* Resultado */}
              <div className="rounded-xl bg-violet-50 border border-violet-100 px-4 py-3 text-center text-violet-700 font-semibold">
                Numerador ÷ Denominador = Tasa de <span className="capitalize">{data.mesReporte}</span>
              </div>
              {/* Exclusiones — al pie, en gris */}
              <p className="text-xs text-gray-400 leading-relaxed pt-1">
                * Algunos procesos se excluyen de ambos grupos:{' '}
                <Link v="exclusiones">reactivados</Link>,{' '}
                <Link v="exclusiones">maduros (+25 sesiones)</Link>{' '}
                y{' '}
                <Link v="exclusiones">derivación exitosa</Link>.
              </p>
            </div>
          </div>
        );

      case 'exclusiones':
        return (
          <div className="flex flex-col gap-4 text-sm text-gray-700 leading-relaxed">
            <p>Algunos procesos se excluyen <strong>de todo el cálculo</strong> — no pueden ser ni numerador ni denominador:</p>
            <div className="rounded-xl bg-blue-50 border border-blue-200 px-4 py-4 flex flex-col gap-2">
              <p className="font-semibold text-blue-800">Reactivados</p>
              <p className="text-blue-700">
                Un proceso que ya pasó a inactivo y fue contabilizado. Si el paciente retoma las sesiones,
                el proceso se <em>reactiva</em> y vuelve a activo — pero no reingresa al cálculo.
                Esto es para no volver a contar el mismo proceso como inactivo nuevamente.
              </p>
              <p className="text-xs text-blue-600 font-medium">Regla: cada proceso solo contribuye una vez a la tasa.</p>
            </div>
            <div className="rounded-xl bg-blue-50 border border-blue-200 px-4 py-4 flex flex-col gap-2">
              <p className="font-semibold text-blue-800">Maduros</p>
              <p className="text-blue-700">
                Procesos con 25 sesiones o más. En frecuencia semanal, eso es aproximadamente 6 meses de trabajo.
                Un proceso largo que llega a su fin natural es un buen indicador.
              </p>
            </div>
            <div className="rounded-xl bg-blue-50 border border-blue-200 px-4 py-4 flex flex-col gap-2">
              <p className="font-semibold text-blue-800">Derivación exitosa</p>
              <p className="text-blue-700">
                El paciente comenzó con su nuevo terapeuta dentro de los 30 días de iniciada su derivación interna.
                Es un cierre planificado y positivo.
              </p>
              <p className="text-xs text-blue-600 font-medium">Si el paciente no inicia con el nuevo terapeuta dentro de los 30 días de iniciada la derivación → el proceso pasa a inactivo → y sí cuenta como parte de tu tasa de inactividad.</p>
            </div>
          </div>
        );

      case 'universo':
        return (
          <div className="flex flex-col gap-4 text-sm text-gray-700 leading-relaxed">
            <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 flex flex-col gap-1">
              <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Denominador</p>
              <p className="font-medium text-gray-800">Procesos activos al inicio de <span className="capitalize">{data.mesEvaluado}</span></p>
            </div>
            <div className="rounded-xl bg-violet-50 border border-violet-100 px-4 py-3 text-sm text-violet-700 font-medium text-center">
              Numerador ÷ Denominador = Tasa de inactividad
            </div>
            <p className="text-xs text-gray-400 leading-relaxed">
              * Se excluyen de este grupo:{' '}
              <Link v="exclusiones">reactivados</Link>,{' '}
              <Link v="exclusiones">maduros (+25 sesiones)</Link>{' '}
              y{' '}
              <Link v="exclusiones">derivación exitosa</Link>.
            </p>
          </div>
        );

      case 'bajas':
        return (
          <div className="flex flex-col gap-4 text-sm text-gray-700 leading-relaxed">
            <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 flex flex-col gap-1">
              <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Numerador</p>
              <p className="font-medium text-gray-800">Procesos que pasaron a inactivo durante el mes evaluado</p>
              <p className="text-sm text-gray-500">Un proceso pasa a inactivo cuando no tiene sesión por 30 días o más.</p>
            </div>
            <div className="rounded-xl bg-violet-50 border border-violet-100 px-4 py-3 text-sm text-violet-700 font-medium text-center">
              Numerador ÷ Denominador = Tasa de inactividad
            </div>
            <p className="text-xs text-gray-400 leading-relaxed">
              * Se excluyen de este grupo:{' '}
              <Link v="exclusiones">reactivados</Link>,{' '}
              <Link v="exclusiones">maduros (25+ sesiones)</Link>{' '}
              y{' '}
              <Link v="exclusiones">derivación exitosa</Link>.
            </p>
          </div>
        );

      case 'excluidos':
        return (
          <div className="flex flex-col gap-4 text-sm text-gray-700 leading-relaxed">
            <p>Algunos procesos no entran ni como denominador ni como numerador.</p>
            <button
              type="button"
              onClick={() => onOpenView('exclusiones')}
              className="w-full text-left rounded-xl bg-gray-50 border border-gray-200 px-4 py-3 hover:bg-gray-100 transition-colors text-sm text-violet-600 font-semibold"
            >
              Ver qué procesos se excluyen y por qué →
            </button>
          </div>
        );

      case 'todos': {
        const allSections = [
          { label: '¿Qué es un proceso terapéutico?', color: 'border-indigo-200 bg-indigo-50 hover:bg-indigo-100', dot: 'bg-indigo-400', view: 'proceso' as PanelView },
          { label: '¿Por qué importa tanto esta métrica?', color: 'border-violet-200 bg-violet-50 hover:bg-violet-100', dot: 'bg-violet-400', view: 'porque' as PanelView },
          { label: '¿Por qué el reporte mide el mes anterior?', color: 'border-sky-200 bg-sky-50 hover:bg-sky-100', dot: 'bg-sky-400', view: 'concordancia' as PanelView },
          { label: '¿Qué procesos se excluyen del cálculo?', color: 'border-orange-200 bg-orange-50 hover:bg-orange-100', dot: 'bg-orange-400', view: 'exclusiones' as PanelView },
        ];
        return (
          <div className="flex flex-col gap-3">
            {allSections.map(s => (
              <button
                key={s.view}
                type="button"
                onClick={() => onOpenView(s.view)}
                className={`rounded-xl border w-full flex items-center gap-3 px-4 py-4 text-left transition-all ${s.color}`}
              >
                <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${s.dot}`} />
                <span className="text-sm font-semibold text-gray-800 flex-1">{s.label}</span>
                <ChevronDown size={16} className="text-gray-400 shrink-0 -rotate-90" />
              </button>
            ))}
            <div className="mt-2 flex flex-col gap-2">
              <p className="text-sm font-semibold text-gray-800">Repasamos todos los conceptos</p>
              <video
                controls
                className="w-full rounded-xl"
                src="/tasa-de-inactividad.mp4"
              />
            </div>
          </div>
        );
      }

      case 'objetivo':
        return (
          <div className="flex flex-col gap-4 text-sm text-gray-700 leading-relaxed">
            <p>
              No exactamente. Tener una tasa por debajo del 10% es la <strong>condición base</strong> para
              que el sistema te priorice en la asignación de nuevos pacientes — pero no es lo único que
              se tiene en cuenta.
            </p>
            <p className="text-gray-500">
              Para entender cómo funciona el sistema completo, entrá a{' '}
              <button
                type="button"
                onClick={() => { onGoToAsignacion?.(); onClose(); }}
                className="text-violet-600 underline underline-offset-2 font-semibold hover:text-violet-800 transition-colors"
              >
                Asignaciones
              </button>
              .
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  // Portal → sale del árbol DOM del componente padre.
  // Así ningún `transform` de un ancestro puede afectar su `position: fixed`.
  return createPortal(
    <div
      className="fixed top-0 right-0 h-full bg-white flex flex-col transition-transform duration-300 ease-in-out"
      style={{
        width: 'min(440px, 100vw)',
        zIndex: 9999,
        transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
        boxShadow: isOpen ? '-4px 0 24px rgba(0,0,0,0.13)' : 'none',
      }}
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-4 border-b border-gray-200 shrink-0">
        {canGoBack && (
          <button
            type="button"
            onClick={onBack}
            className="rounded-lg p-1.5 hover:bg-gray-100 transition-colors shrink-0 text-gray-500 hover:text-gray-800"
            aria-label="Volver"
          >
            <ChevronDown size={18} className="rotate-90" />
          </button>
        )}
        <h2 className="text-base font-semibold text-gray-800 leading-snug flex-1">{title}</h2>
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg p-1.5 hover:bg-gray-100 transition-colors shrink-0"
          aria-label="Cerrar"
        >
          <X size={16} className="text-gray-500" />
        </button>
      </div>

      {/* Contenido — ref para scroll-to-top automático */}
      <div ref={scrollRef} className="overflow-y-auto flex-1 px-5 py-5">
        {renderContent()}
      </div>
    </div>,
    document.body,
  );
};

// ─── Detalle sub-components ───────────────────────────────────────────────────

const HelpLink: React.FC<{ label?: string; onClick: () => void }> = ({ label = '¿qué significa esto?', onClick }) => (
  <button
    type="button"
    onClick={e => { e.stopPropagation(); onClick(); }}
    className="text-xs text-violet-500 underline underline-offset-2 hover:text-violet-700 transition-colors ml-1"
  >
    {label}
  </button>
);

const DetalleDenominador: React.FC<{ processes: InactividadProcess[]; mesEvaluado: string; onHelp: () => void }> = ({ processes, mesEvaluado, onHelp }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-gray-100 bg-white overflow-hidden">
      <button type="button" onClick={() => setOpen(v => !v)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors">
        <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
          <div className="bg-violet-100 rounded-lg p-1 shrink-0"><Users size={13} className="text-violet-600" /></div>
          <span className="text-sm font-medium text-gray-800">
            Procesos activos al inicio de <span className="capitalize">{mesEvaluado}</span>
          </span>
          <HelpLink onClick={onHelp} />
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="text-sm font-semibold text-gray-700">{processes.length}</span>
          {open ? <ChevronUp size={15} className="text-gray-400" /> : <ChevronDown size={15} className="text-gray-400" />}
        </div>
      </button>
      {open && (
        <div className="border-t border-gray-100 overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Paciente</TableHead><TableHead className="text-right">Sesiones</TableHead><TableHead>Última sesión</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {processes.map(p => (
                <TableRow key={p.id}>
                  <TableCell className="font-medium text-gray-800">{p.patientName}</TableCell>
                  <TableCell className="text-right text-gray-700">{p.sessionCount}</TableCell>
                  <TableCell className="text-gray-500">{p.lastSessionDate}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

const DetalleBajas: React.FC<{ bajas: InactividadBaja[]; mesEvaluado: string; onHelp: () => void }> = ({ bajas, mesEvaluado, onHelp }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-red-100 bg-white overflow-hidden">
      <button type="button" onClick={() => setOpen(v => !v)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-red-50 transition-colors">
        <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
          <div className="bg-red-100 rounded-lg p-1 shrink-0"><TrendingDown size={13} className="text-red-500" /></div>
          <span className="text-sm font-medium text-gray-800">
            Procesos que pasaron a inactivo durante <span className="capitalize">{mesEvaluado}</span>
          </span>
          <HelpLink onClick={onHelp} />
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="text-sm font-semibold text-gray-700">{bajas.length}</span>
          {open ? <ChevronUp size={15} className="text-gray-400" /> : <ChevronDown size={15} className="text-gray-400" />}
        </div>
      </button>
      {open && (
        <div className="border-t border-red-100 overflow-x-auto">
          {bajas.length === 0
            ? <p className="text-sm text-gray-400 text-center py-4">Sin procesos que pasaron a inactivo en <span className="capitalize">{mesEvaluado}</span>. 🎉</p>
            : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Paciente</TableHead><TableHead className="text-right">Sesiones</TableHead><TableHead>Última sesión</TableHead><TableHead>Motivo</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {bajas.map(b => (
                    <TableRow key={b.id}>
                      <TableCell className="font-medium text-gray-800">{b.patientName}</TableCell>
                      <TableCell className="text-right text-gray-700">{b.sessionCount}</TableCell>
                      <TableCell className="text-gray-500">{b.lastSessionDate}</TableCell>
                      <TableCell><Badge className="text-xs font-medium bg-red-100 text-red-700">{b.reason}</Badge></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
        </div>
      )}
    </div>
  );
};

const DetalleExcluidos: React.FC<{ excluded: InactividadExcluido[]; onHelp: () => void; onReasonClick?: () => void }> = ({ excluded, onHelp, onReasonClick }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-gray-100 bg-white overflow-hidden">
      <button type="button" onClick={() => setOpen(v => !v)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors">
        <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
          <div className="bg-gray-100 rounded-lg p-1 shrink-0"><XCircle size={13} className="text-gray-500" /></div>
          <span className="text-sm font-medium text-gray-800">Excluidos del universo</span>
          <HelpLink onClick={onHelp} />
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="text-sm font-semibold text-gray-700">{excluded.length}</span>
          {open ? <ChevronUp size={15} className="text-gray-400" /> : <ChevronDown size={15} className="text-gray-400" />}
        </div>
      </button>
      {open && excluded.length > 0 && (
        <div className="border-t border-gray-100 overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Paciente</TableHead><TableHead>Excluido por</TableHead><TableHead className="text-right">Sesiones</TableHead><TableHead>Última sesión</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {excluded.map(e => (
                <TableRow key={e.id}>
                  <TableCell className="font-medium text-gray-800">{e.patientName}</TableCell>
                  <TableCell>
                    <button
                      type="button"
                      onClick={ev => { ev.stopPropagation(); onReasonClick?.(); }}
                      className="text-xs text-violet-600 underline underline-offset-2 hover:text-violet-800 transition-colors font-medium"
                    >
                      {e.exclusionReason}
                    </button>
                  </TableCell>
                  <TableCell className="text-right text-gray-700">{e.sessionCount}</TableCell>
                  <TableCell className="text-gray-500">{e.lastSessionDate}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

interface HistoricoRowProps {
  mesReporte: string;
  mesEvaluado: string;
  tasa: number;
  bajas: InactividadBaja[];
  denominador: InactividadProcess[];
  excluidos: InactividadExcluido[];
  onHelpBajas: () => void;
  onHelpDenominador: () => void;
  onHelpExcluidos: () => void;
}

const HistoricoRow: React.FC<HistoricoRowProps> = ({
  mesReporte, mesEvaluado, tasa, bajas, denominador, excluidos,
  onHelpBajas, onHelpDenominador, onHelpExcluidos,
}) => {
  const [open, setOpen] = useState(false);
  const ok = isRateOk(tasa);
  return (
    <div className="rounded-xl border border-gray-100 bg-white overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors text-left"
      >
        <span className="text-sm font-medium text-gray-800 capitalize">{mesReporte}</span>
        <div className="flex items-center gap-3">
          <span className={`text-sm font-semibold ${ok ? 'text-emerald-600' : 'text-red-500'}`}>
            {formatRate(tasa)}
          </span>
          {open ? <ChevronUp size={15} className="text-gray-400 shrink-0" /> : <ChevronDown size={15} className="text-gray-400 shrink-0" />}
        </div>
      </button>
      {open && (
        <div className="border-t border-gray-100 px-3 pb-3 pt-2 flex flex-col gap-2">
          <DetalleBajas bajas={bajas} mesEvaluado={mesEvaluado} onHelp={onHelpBajas} />
          <DetalleDenominador processes={denominador} mesEvaluado={mesEvaluado} onHelp={onHelpDenominador} />
          <DetalleExcluidos excluded={excluidos} onHelp={onHelpExcluidos} />
        </div>
      )}
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

interface MetricsInactividadTabProps {
  onGoToAsignacion?: () => void;
}

export const MetricsInactividadTab: React.FC<MetricsInactividadTabProps> = ({ onGoToAsignacion }) => {
  const [selectedMonth, setSelectedMonth] = useState<MonthData>(AVAILABLE_MONTHS[0]);
  const [monthDropdownOpen, setMonthDropdownOpen] = useState(false);

  const { psychologistId } = useAuth();
  const { psychologist } = usePsychologistContext();
  const isPendingDischarged = psychologist?.statusId === PENDING_DISCHARGED_STATUS_ID;
  const [sheetData, setSheetData] = useState<InactivityRateData | null>(null);
  const [sheetLoading, setSheetLoading] = useState(false);

  const [detailData, setDetailData] = useState<InactivityDetailData | null>(null);
  const [detailDataPrev2, setDetailDataPrev2] = useState<InactivityDetailData | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const detailFetchedRef = useRef(false);

  // Pre-carga al montar y al cambiar de mes
  useEffect(() => {
    if (!psychologistId) return;
    setSheetLoading(true);
    setSheetData(null);
    fetchInactivityRate(psychologistId, prevMonthYYYYMM(monthLabelToYYYYMM(selectedMonth.monthLabel)))
      .then(setSheetData)
      .catch((_err) => { /* fallback al mock */ })
      .finally(() => setSheetLoading(false));
  }, [psychologistId, selectedMonth.monthLabel]);

  useEffect(() => {
    setDetailData(null);
    setDetailDataPrev2(null);
    detailFetchedRef.current = false;
  }, [selectedMonth.monthLabel]);

  const fetchDetail = useCallback(() => {
    if (!psychologistId || detailFetchedRef.current || detailLoading) return;
    const m1 = prevMonthYYYYMM(monthLabelToYYYYMM(selectedMonth.monthLabel));
    const m2 = prevMonthYYYYMM(m1);
    const [year1, month1] = m1.split('-');
    const [year2, month2] = m2.split('-');
    detailFetchedRef.current = true;
    setDetailLoading(true);
    Promise.all([
      fetchInactivityDetail(psychologistId, month1, year1),
      fetchInactivityDetail(psychologistId, month2, year2),
    ])
      .then(([d1, d2]) => { setDetailData(d1); setDetailDataPrev2(d2); })
      .catch((_err) => { /* fallback al mock */ })
      .finally(() => setDetailLoading(false));
  }, [psychologistId, selectedMonth.monthLabel, detailLoading]);

  // Precarga el detalle para tener números en "cómo se calcula"
  useEffect(() => {
    if (psychologistId) fetchDetail();
  }, [psychologistId, selectedMonth.monthLabel]); // eslint-disable-line react-hooks/exhaustive-deps

  const [showHistorico, setShowHistorico] = useState(false);
  const [showCalcExpanded, setShowCalcExpanded] = useState(false);
  const calcSectionRef = useRef<HTMLDivElement>(null);

  // Panel — stack de navegación para el botón "atrás"
  const [panelStack, setPanelStack] = useState<PanelView[]>([]);
  const panelView = panelStack[panelStack.length - 1] ?? null;
  const closePanel = useCallback(() => setPanelStack([]), []);
  const pushPanel = useCallback((v: PanelView) => {
    if (v) setPanelStack(prev => [...prev, v]);
  }, []);
  const popPanel = useCallback(() => {
    setPanelStack(prev => prev.slice(0, -1));
  }, []);
  const canGoBack = panelStack.length > 1;

  const data = selectedMonth;

  // pending_discharged queda fuera del roster que evalúa fetchInactivityRate — para esas psicos
  // la tasa sale directo del detalle, que sí las procesa (ver rateFromDetail).
  const displayRate: number | null = isPendingDischarged
    ? (detailData ? rateFromDetail(detailData) : null)
    : (sheetData?.deregistration_rate ?? null);
  const rateLoading = isPendingDischarged ? detailLoading : sheetLoading;
  const rateOk = displayRate !== null && isRateOk(displayRate);

  // Números para la fórmula
  const formulaNumerador = detailData?.numerador ?? data.numerator.length;
  const formulaDenominador = detailData?.denominador ?? data.denominator.length;

  // Detail arrays — derivados de detailData.procesos filtrando por rol_en_calculo
  const bajas: InactividadBaja[] = detailData
    ? detailData.procesos
        .filter(p => p.rol_en_calculo === 'numerador')
        .map(p => ({
          id: p.process,
          patientName: patientName(p),
          lastSessionDate: formatISODate(p.ultima_sesion_date_absoluta),
          sessionCount: sessionCount(p),
          reason: 'Inactivo por tiempo' as BajaReason,
        }))
    : data.numerator;

  const denominador: InactividadProcess[] = detailData
    ? detailData.procesos
        .filter(p => p.rol_en_calculo === 'denominador' || p.rol_en_calculo === 'numerador')
        .map(p => ({
          id: p.process,
          patientName: patientName(p),
          lastSessionDate: formatISODate(p.ultima_sesion_date_absoluta),
          sessionCount: sessionCount(p),
        }))
    : data.denominator;

  const excluidos: InactividadExcluido[] = detailData
    ? (() => {
        const fromM1: InactividadExcluido[] = detailData.procesos
          .filter(p => p.rol_en_calculo?.startsWith('excluido'))
          .map(p => ({
            id: p.process,
            patientName: patientName(p),
            exclusionReason: ROL_TO_EXCLUSION[p.rol_en_calculo] ?? 'Proceso maduro',
            sessionCount: sessionCount(p),
            lastSessionDate: formatISODate(p.ultima_sesion_date_absoluta),
          }));
        const seenIds = new Set(fromM1.map(e => e.id));
        const fromM2: InactividadExcluido[] = (detailDataPrev2?.procesos ?? [])
          .filter(p => p.es_reactivado === 1 && !seenIds.has(p.process))
          .map(p => ({
            id: p.process,
            patientName: patientName(p),
            exclusionReason: 'Reactivado' as ExclusionReason,
            sessionCount: sessionCount(p),
            lastSessionDate: formatISODate(p.ultima_sesion_date_absoluta),
          }));
        return [...fromM1, ...fromM2];
      })()
    : data.excluded;


  // Helper for clickable terms
  const Term: React.FC<{ v: PanelView; children: React.ReactNode }> = ({ v, children }) => (
    <button
      type="button"
      onClick={() => pushPanel(v)}
      className="text-violet-600 underline underline-offset-2 font-semibold hover:text-violet-800 transition-colors"
    >
      {children}
    </button>
  );

  return (
    <div className="flex flex-col gap-5">

      {/* ── 1. Intro ─────────────────────────────────────────────────────── */}
      <div className="rounded-2xl bg-violet-50 border border-violet-100 px-5 py-4 flex flex-col gap-2">
        <p className="text-base font-bold text-gray-900">Tu tasa de inactividad</p>
        <p className="text-sm text-gray-700 leading-relaxed">
          Mide, mes a mes, cuántos{' '}
          <Term v="proceso">procesos terapéuticos</Term>{' '}
          pasaron{' '}
          <Term v="proceso">de activo a inactivo</Term>
          . Es uno de los factores más importantes que define cuántos pacientes nuevos podés recibir.
        </p>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => pushPanel('objetivo')}
            className="text-sm text-violet-700 font-semibold hover:text-violet-900 underline underline-offset-2 transition-colors text-left"
          >
            Objetivo: mantenerla por debajo del 10% mensual →
          </button>
          <button
            type="button"
            onClick={() => pushPanel('porque')}
            className="text-xs text-violet-500 underline underline-offset-2 hover:text-violet-700 transition-colors"
          >
            ¿Por qué es tan importante? →
          </button>
        </div>
      </div>

      {/* ── 2. Selector de mes ───────────────────────────────────────────── */}
      <div className="flex justify-end">
        <div className="relative">
          <button
            type="button"
            onClick={() => setMonthDropdownOpen(v => !v)}
            className="flex items-center gap-2 px-3.5 py-1.5 bg-white border border-gray-200 rounded-xl text-sm font-semibold text-gray-800 shadow-sm hover:border-violet-300 hover:bg-violet-50 transition-all capitalize"
          >
            {selectedMonth.monthLabel}
            <ChevronDown size={13} className={`text-gray-400 transition-transform duration-200 ${monthDropdownOpen ? 'rotate-180' : ''}`} />
          </button>
          {monthDropdownOpen && (
            <div className="absolute top-full mt-1 right-0 z-20 bg-white rounded-xl shadow-lg border border-gray-100 py-1 min-w-[160px]">
              {AVAILABLE_MONTHS.map(m => (
                <button
                  key={m.monthLabel}
                  type="button"
                  onClick={() => { setSelectedMonth(m); setMonthDropdownOpen(false); }}
                  className={`w-full flex items-center justify-between text-left px-4 py-2.5 text-sm capitalize transition-colors ${
                    selectedMonth.monthLabel === m.monthLabel ? 'text-violet-700 font-semibold bg-violet-50' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {m.monthLabel}
                  {selectedMonth.monthLabel === m.monthLabel && <Check size={13} className="text-violet-600" />}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── 3. Burbuja principal ─────────────────────────────────────────── */}
      <div className={`rounded-2xl px-6 pt-6 pb-5 flex flex-col items-center gap-3 ${
        rateOk
          ? 'bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-100'
          : 'bg-gradient-to-br from-red-50 to-rose-50 border border-red-100'
      }`}>
        {/* Título */}
        <p className="text-sm font-semibold text-gray-700 capitalize">
          Tasa de inactividad de {data.mesReporte}
        </p>
        {/* Aclaración italic clickeable */}
        <button
          type="button"
          onClick={() => pushPanel('concordancia')}
          className="text-xs italic text-gray-400 underline underline-offset-2 hover:text-gray-600 transition-colors -mt-2"
        >
          mide la actividad de {data.mesEvaluado}
        </button>
        {/* Porcentaje */}
        {rateLoading ? (
          <div className="h-14 w-32 rounded-xl bg-gray-200 animate-pulse" />
        ) : displayRate === null ? (
          <span className="text-5xl font-bold text-gray-300">—</span>
        ) : (
          <span className={`text-5xl font-bold ${rateOk ? 'text-emerald-600' : 'text-red-500'}`}>
            {formatRate(displayRate)}
          </span>
        )}
        {/* Indicador */}
        {(!rateLoading && displayRate !== null) && (
          <button
            type="button"
            onClick={() => pushPanel('objetivo')}
            className="text-xs text-gray-400 hover:text-violet-500 underline underline-offset-2 transition-colors"
          >
            {rateOk ? '✓ Dentro del objetivo (≤ 10%)' : '⚠ Por encima del objetivo (> 10%)'}
          </button>
        )}
        {(rateLoading || displayRate === null) && <span className="text-xs text-gray-400"> </span>}
        {/* Ver detalle */}
        <button
          type="button"
          onClick={() => {
            setShowCalcExpanded(true);
            setTimeout(() => calcSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
          }}
          className="text-xs text-violet-500 underline underline-offset-2 hover:text-violet-700 transition-colors"
        >
          Ver detalle →
        </button>
      </div>

      {/* ── 4+5. Cómo se calcula (sección unificada) ────────────────────── */}
      <div ref={calcSectionRef} className="rounded-2xl border-2 border-violet-200 bg-white overflow-hidden">
        <button
          type="button"
          onClick={() => setShowCalcExpanded(v => !v)}
          aria-expanded={showCalcExpanded}
          className="w-full flex items-center justify-center gap-3 px-6 py-5 hover:bg-violet-50 transition-colors"
        >
          <span className="text-base font-semibold text-violet-700">¿Cómo se calcula?</span>
          <ChevronDown size={18} className={`text-violet-400 transition-transform duration-200 ${showCalcExpanded ? 'rotate-180' : ''}`} />
        </button>

        {showCalcExpanded && (
          <div className="border-t border-violet-100 px-6 pb-8 pt-6 flex flex-col items-center gap-6 text-sm text-gray-600">

            {/* Fórmula con números reales + sub-labels de mes */}
            <div className="flex items-center justify-center gap-5">
              <div className="text-center">
                <p className="text-xs text-gray-400 mb-1">Pasaron a inactivo</p>
                <p className="text-3xl font-bold text-gray-800">{formulaNumerador}</p>
                <button
                  type="button"
                  onClick={() => pushPanel('concordancia')}
                  className="text-xs text-gray-400 hover:text-violet-500 underline underline-offset-2 transition-colors mt-1 capitalize"
                >
                  durante el mes de {data.mesEvaluado}
                </button>
              </div>
              <p className="text-3xl text-gray-300 font-light">÷</p>
              <div className="text-center">
                <p className="text-xs text-gray-400 mb-1">Activos al inicio</p>
                <p className="text-3xl font-bold text-gray-800">{formulaDenominador}</p>
                <button
                  type="button"
                  onClick={() => pushPanel('concordancia')}
                  className="text-xs text-gray-400 hover:text-violet-500 underline underline-offset-2 transition-colors mt-1 capitalize"
                >
                  al inicio del mes de {data.mesEvaluado}
                </button>
              </div>
              <p className="text-3xl text-gray-300 font-light">=</p>
              <div className="text-center">
                <p className="text-xs text-gray-400 mb-1">Tasa</p>
                <p className={`text-3xl font-bold ${displayRate !== null ? (rateOk ? 'text-emerald-600' : 'text-red-500') : 'text-gray-300'}`}>
                  {displayRate !== null ? formatRate(displayRate) : formatRate(data.rate)}
                </p>
              </div>
            </div>

            {/* Nota de concordancia — inmediatamente debajo del resultado */}
            <p className="text-xs text-gray-400 text-center max-w-md leading-relaxed -mt-2">
              La tasa de <span className="capitalize font-medium text-gray-500">{data.mesReporte}</span> se calcula con los datos de{' '}
              <button type="button" onClick={() => pushPanel('concordancia')} className="underline underline-offset-2 hover:text-gray-600 transition-colors">
                <span className="capitalize">{data.mesEvaluado}</span>
              </button>
              , porque necesitamos que el mes cierre antes de poder medirlo.
            </p>

            {/* Acordeones de detalle — Numerador, Denominador, Excluidos */}
            <div className="w-full flex flex-col gap-2">
              {detailLoading ? (
                <div className="flex items-center justify-center py-6 gap-2 text-sm text-gray-400">
                  <div className="w-4 h-4 rounded-full border-2 border-violet-300 border-t-violet-600 animate-spin" />
                  Cargando detalle…
                </div>
              ) : (
                <>
                  <DetalleBajas bajas={bajas} mesEvaluado={data.mesEvaluado} onHelp={() => pushPanel('bajas')} />
                  <DetalleDenominador processes={denominador} mesEvaluado={data.mesEvaluado} onHelp={() => pushPanel('universo')} />
                  <DetalleExcluidos excluded={excluidos} onHelp={() => pushPanel('exclusiones')} onReasonClick={() => pushPanel('exclusiones')} />
                </>
              )}
            </div>

            {/* Exclusiones al pie, en gris */}
            <p className="text-xs text-gray-400 leading-relaxed px-1 w-full">
              * Se excluyen de ambos grupos:{' '}
              <Term v="exclusiones">reactivados</Term>,{' '}
              <Term v="exclusiones">maduros</Term>{' '}
              y{' '}
              <Term v="exclusiones">derivación exitosa</Term>.
            </p>

          </div>
        )}
      </div>

      {/* ── 6. Histórico ─────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-3">
        <button
          type="button"
          onClick={() => setShowHistorico(v => !v)}
          className="flex items-center justify-between w-full px-4 py-3 rounded-xl border border-gray-100 bg-white hover:bg-gray-50 transition-colors text-left"
          aria-expanded={showHistorico}
        >
          <div className="flex items-center gap-2">
            <BarChart2 size={15} className="text-violet-500" />
            <span className="text-sm font-medium text-gray-800">Ver comparación mes a mes</span>
          </div>
          {showHistorico ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
        </button>
        {showHistorico && (
          <div className="flex flex-col gap-2">
            {displayRate !== null ? (
              <HistoricoRow
                mesReporte={data.mesReporte}
                mesEvaluado={data.mesEvaluado}
                tasa={displayRate}
                bajas={bajas}
                denominador={denominador}
                excluidos={excluidos}
                onHelpBajas={() => pushPanel('bajas')}
                onHelpDenominador={() => pushPanel('universo')}
                onHelpExcluidos={() => pushPanel('exclusiones')}
              />
            ) : (
              <p className="text-center text-gray-400 py-4 text-sm">
                {rateLoading ? 'Cargando…' : 'Sin datos disponibles aún.'}
              </p>
            )}
          </div>
        )}
      </div>

      {/* ── 7. Repasá todos los conceptos ───────────────────────────────── */}
      <button
        type="button"
        onClick={() => pushPanel('todos')}
        className="w-full py-4 rounded-2xl bg-violet-600 hover:bg-violet-700 active:bg-violet-800 text-white text-sm font-semibold shadow-sm transition-all"
      >
        Repasá todos los conceptos →
      </button>

      {/* ── Side Panel ── Portal en document.body, nunca afectado por transforms padres ── */}
      <SidePanel
        view={panelView}
        onClose={closePanel}
        onBack={popPanel}
        canGoBack={canGoBack}
        data={data}
        onOpenView={pushPanel}
        onGoToAsignacion={onGoToAsignacion}
      />

    </div>
  );
};

export default MetricsInactividadTab;
